import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Loader2, Search, CheckCircle2, Clock, Send, ChevronRight, Trash2, RotateCcw } from "lucide-react";
import { canDeleteReport, deleteReportWithUndo, restoreReport } from "@/components/staff/reportActions";

const ReportsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const [form, setForm] = useState({ patient_id: "", report_type: "participant", title: "", content: "" });
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => { fetchPatients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReports(); }, [statusFilter, typeFilter, debounced, showDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReports = async () => {
    setLoading(true);
    try {
      let url = `${API}/staff/all-reports?limit=50${showDeleted ? "&deleted=true" : ""}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (typeFilter !== "all") url += `&report_type=${typeFilter}`;
      if (debounced) url += `&q=${encodeURIComponent(debounced)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setReports(d.reports); setTotal(d.total); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchPatients = async () => {
    try {
      const res = await fetch(`${API}/admin/users?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPatients((d.users || d).filter(u => u.role === "patient")); }
    } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    if (!form.patient_id || !form.title) { toast.error("Please select a patient and enter a title."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/staff/reports`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.ok) { toast.success("Report created."); setShowNew(false); setForm({ patient_id: "", report_type: "participant", title: "", content: "" }); fetchReports(); }
      else toast.error("Failed to create report.");
    } catch { toast.error("Connection error."); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      const res = await fetch(`${API}/staff/reports/${reportId}/status?status=${newStatus}`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast.success(`Report ${newStatus}.`); fetchReports(); }
    } catch { toast.error("Failed to update."); }
  };

  const statusColor = { draft: "bg-yellow-50 text-yellow-700 border-yellow-200", reviewed: "bg-blue-50 text-blue-700 border-blue-200", approved: "bg-green-50 text-green-700 border-green-200", sent: "bg-purple-50 text-purple-700 border-purple-200" };
  const statusIcon = { draft: Clock, reviewed: CheckCircle2, approved: CheckCircle2, sent: Send };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">Clinical Reports</h1>
          <p className="mt-1 text-base text-stone-500">{total} {total === 1 ? "report" : "reports"}. Participant and doctor reports with approval workflow.</p>
        </div>
        <Button onClick={() => setShowNew(!showNew)} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="new-report-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Report
        </Button>
      </div>

      {showNew && (
        <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-4" data-testid="report-form">
          <h3 className="font-bold text-stone-900">Create clinical report</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Select value={form.patient_id} onValueChange={v => setForm({ ...form, patient_id: v })}>
              <SelectTrigger className="rounded-lg"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.email})</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.report_type} onValueChange={v => setForm({ ...form, report_type: v })}>
              <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="participant">Participant report</SelectItem>
                <SelectItem value="doctor">Doctor report</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Report title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-lg" />
          </div>
          <Textarea placeholder="Report content..." value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="rounded-lg min-h-[120px]" rows={5} />
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save as draft
            </Button>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 items-center" data-testid="reports-filters">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by patient name" className="pl-9 rounded-lg" data-testid="reports-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44 rounded-lg" data-testid="reports-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="participant">Patient report</SelectItem>
            <SelectItem value="doctor">Doctor report</SelectItem>
            <SelectItem value="progress">Progress report</SelectItem>
                      </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-lg" data-testid="reports-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={showDeleted ? "default" : "outline"} size="sm" className={`text-xs rounded-lg ${showDeleted ? "bg-stone-900 text-white" : ""}`} onClick={() => setShowDeleted(v => !v)} data-testid="reports-recently-deleted"><Trash2 className="w-3.5 h-3.5 mr-1" />Recently deleted</Button>
        {(search || typeFilter !== "all" || statusFilter !== "all") && (
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setSearch(""); setTypeFilter("all"); setStatusFilter("all"); }} data-testid="reports-clear-filters">Clear</Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : reports.length > 0 ? (
        <div className="space-y-3">
          {reports.map(r => (
            <Card key={r.report_id} onClick={() => !showDeleted && navigate(`/admin/patients/${r.patient_id}?report=${r.report_id}`)}
              className="rounded-xl border-stone-200/60 p-5 cursor-pointer hover:border-[#FF7A00]/40 hover:shadow-md transition-[border-color,box-shadow] group" data-testid={`report-card-${r.report_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-stone-900">{r.title || "Untitled report"}</p>
                    <Badge variant="outline" className={`text-xs capitalize ${statusColor[r.status] || ""}`}>{r.status}</Badge>
                    <Badge variant="outline" className="text-xs capitalize">{r.report_type}</Badge>
                  </div>
                  <p className="text-xs text-stone-400 mt-0.5">Patient: {r.patient_name} &middot; By: {r.created_by_name} &middot; {r.created_at?.split("T")[0]}</p>
                </div>
                <div className="flex gap-2 items-center" onClick={e => e.stopPropagation()}>
                  {showDeleted ? (<>
                    <span className="text-xs text-stone-400">Deleted {r.deleted_at?.split("T")[0]}{r.deleted_by_name ? ` by ${r.deleted_by_name}` : ""}</span>
                    <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => restoreReport(token, r.report_id, fetchReports)} data-testid={`restore-report-${r.report_id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" />Restore</Button>
                  </>) : (<>
                  {r.status === "draft" && <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => handleStatusChange(r.report_id, "reviewed")}>Mark Reviewed</Button>}
                  {r.status === "reviewed" && <Button size="sm" className="text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white" onClick={() => handleStatusChange(r.report_id, "approved")}>Approve</Button>}
                  {r.status === "approved" && <Button size="sm" className="text-xs rounded-lg bg-purple-600 hover:bg-purple-700 text-white" onClick={() => handleStatusChange(r.report_id, "sent")}>Mark Sent</Button>}
                  {canDeleteReport(r) && <Button size="sm" variant="ghost" className="text-xs text-red-600 hover:bg-red-50 px-2" onClick={() => deleteReportWithUndo(token, r, fetchReports)} data-testid={`delete-report-${r.report_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  <Button size="sm" variant="ghost" className="text-xs text-[#FF7A00]" onClick={() => navigate(`/admin/patients/${r.patient_id}?report=${r.report_id}`)} data-testid={`open-report-${r.report_id}`}>Open<ChevronRight className="w-3.5 h-3.5 ml-0.5 transition-transform group-hover:translate-x-0.5" /></Button>
                  </>)}
                </div>
              </div>
              {r.content && <p className="mt-3 text-sm text-stone-600 whitespace-pre-wrap line-clamp-3">{r.content}</p>}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-xl border-stone-200/60 p-12 text-center">
          <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500" data-testid="reports-empty">{showDeleted ? "No reports deleted in the last 30 days." : (debounced || typeFilter !== "all" || statusFilter !== "all") ? "No reports match your search or filters." : "No clinical reports yet. Create one from a patient profile or using the button above."}</p>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
